import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOMeta from "../../components/base/SEOMeta";
import AdSlot from "../../components/base/AdSlot";
import { categories, trendingTags } from "../../mocks/categories";
import { allQuizzes } from "../../utils/allQuizzes";
import type { Quiz } from "../../utils/allQuizzes";

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function formatPlays(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

function QuizCard({ quiz }: { quiz: Quiz }) {
  return (
    <Link
      to={`/quiz/${quiz.id}`}
      className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#FF6B6B]/30 hover:-translate-y-1 transition-all duration-200 cursor-pointer"
    >
      <div className="w-full h-44 overflow-hidden relative">
        <img
          src={quiz.image}
          alt={quiz.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 right-3 bg-white/90 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full">
          {quiz.estimatedTime}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold text-[#FF6B6B] uppercase tracking-wider">
            {quiz.categoryLabel}
          </span>
          <span className="text-xs bg-orange-50 text-orange-400 px-2 py-0.5 rounded-full font-medium">
            {quiz.tag}
          </span>
        </div>
        <h3
          className="font-extrabold text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-[#FF6B6B] transition-colors mb-3"
          style={{ fontFamily: "'Nunito', sans-serif" }}
        >
          {quiz.title}
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <i className="ri-user-line" />
            <span>{formatPlays(quiz.totalPlays)} plays</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-amber-500">
            <i className="ri-star-fill" />
            <span>{quiz.rating}</span>
          </div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 text-[#FF6B6B] text-sm font-bold group-hover:gap-3 transition-all">
          <span>Start Quiz</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    </Link>
  );
}

export default function CategoriesPage() {
  const [searchParams] = useSearchParams();
  const catParam = searchParams.get("cat");
  const tagParam = searchParams.get("tag");

  const [activeCategory, setActiveCategory] = useState<string>(catParam || "all");
  const [activeTag, setActiveTag] = useState<string>(tagParam || "");

  useEffect(() => {
    if (catParam) setActiveCategory(catParam);
    if (tagParam) {
      setActiveTag(tagParam);
      setActiveCategory("all");
    }
  }, [catParam, tagParam]);

  const filtered = allQuizzes.filter((q) => {
    const matchCat = activeCategory === "all" || q.category === activeCategory;
    const matchTag = !activeTag || q.tag.toLowerCase().replace(/\s+/g, "-") === activeTag;
    return matchCat && matchTag;
  });

  // Split filtered quizzes into groups of 6 for in-feed ads
  const quizGroups: (Quiz | "ad")[] = [];
  filtered.forEach((q, idx) => {
    quizGroups.push(q);
    if ((idx + 1) % 6 === 0 && idx + 1 < filtered.length) {
      quizGroups.push("ad");
    }
  });

  const currentCatLabel =
    activeCategory === "all"
      ? "All Quizzes"
      : categories.find((c) => c.slug === activeCategory)?.label || "All Quizzes";

  const pageTitle = activeTag
    ? `${trendingTags.find((t) => t.slug === activeTag)?.label || activeTag} Quizzes | QuizVibe`
    : `${currentCatLabel} – Browse Personality Tests & Viral Quizzes | QuizVibe`;

  const pageDescription = `Browse fun personality quizzes${activeTag ? ` tagged ${trendingTags.find((t) => t.slug === activeTag)?.label || activeTag}` : activeCategory !== "all" ? ` in ${currentCatLabel}` : ""} on QuizVibe. Discover viral tests for kids and teens – updated every week!`;

  const categoriesJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageTitle,
      "description": pageDescription,
      "url": `${SITE_URL}/categories`,
      "inLanguage": "en",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Quiz Categories", "item": `${SITE_URL}/categories` }
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "QuizVibe",
        "url": SITE_URL
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "QuizVibe Quiz List",
      "url": `${SITE_URL}/categories`,
      "numberOfItems": filtered.length,
      "itemListElement": filtered.slice(0, 10).map((q, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": q.title,
        "url": `${SITE_URL}/quiz/${q.id}`
      }))
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <SEOMeta
        title={pageTitle}
        description={pageDescription}
        keywords={`quiz categories, personality quiz, kids quiz, teens quiz, ${activeTag ? trendingTags.find((t) => t.slug === activeTag)?.label : currentCatLabel}`}
        canonicalPath="/categories"
        jsonLd={categoriesJsonLd}
      />
      <Navbar />

      {/* Page Header */}
      <div className="pt-24 pb-8 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span>Quiz Categories</span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-extrabold text-white mb-2"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {activeTag
              ? `#${trendingTags.find((t) => t.slug === activeTag)?.label || activeTag}`
              : currentCatLabel}
          </h1>
          <p className="text-white/80 text-sm">
            {filtered.length} quizzes available · Updated weekly
          </p>
        </div>
      </div>

      {/* ── Ad: Leaderboard below header ── */}
      <div className="w-full bg-white border-b border-gray-100 py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-center">
          <div className="hidden md:block">
            <AdSlot size="leaderboard" />
          </div>
          <div className="md:hidden w-full flex justify-center">
            <AdSlot size="mobile-banner" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            {/* Categories Filter */}
            <div className="bg-white rounded-2xl p-5 mb-5">
              <h3 className="font-extrabold text-gray-900 text-sm mb-4 uppercase tracking-wider">
                Categories
              </h3>
              <ul className="space-y-1">
                <li>
                  <button
                    onClick={() => { setActiveCategory("all"); setActiveTag(""); }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer text-left ${
                      activeCategory === "all" && !activeTag
                        ? "bg-[#FF6B6B] text-white"
                        : "text-gray-600 hover:bg-orange-50 hover:text-[#FF6B6B]"
                    }`}
                  >
                    <span>All Quizzes</span>
                    <span className="text-xs opacity-70">{allQuizzes.length}</span>
                  </button>
                </li>
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <button
                      onClick={() => { setActiveCategory(cat.slug); setActiveTag(""); }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer text-left ${
                        activeCategory === cat.slug && !activeTag
                          ? "bg-[#FF6B6B] text-white"
                          : "text-gray-600 hover:bg-orange-50 hover:text-[#FF6B6B]"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <i className={cat.icon} />
                        <span>{cat.label}</span>
                      </div>
                      <span className="text-xs opacity-70">{cat.quizCount}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trending Tags Filter */}
            <div className="bg-white rounded-2xl p-5 mb-5">
              <h3 className="font-extrabold text-gray-900 text-sm mb-4 uppercase tracking-wider">
                Trending Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {trendingTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => {
                      setActiveTag(activeTag === tag.slug ? "" : tag.slug);
                      setActiveCategory("all");
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                      activeTag === tag.slug
                        ? "bg-[#FF6B6B] border-[#FF6B6B] text-white"
                        : "border-gray-200 text-gray-600 hover:border-[#FF6B6B] hover:text-[#FF6B6B]"
                    }`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Ad: Sidebar rectangle 300×250 ── */}
            <div className="flex justify-center mb-5">
              <AdSlot size="rectangle" />
            </div>

            {/* ── Ad: Sidebar rectangle 300×250 (second slot, sticky) ── */}
            <div className="sticky top-24 flex justify-center">
              <AdSlot size="large-rectangle" />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Mobile Filters */}
            <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto pb-2">
              <button
                onClick={() => { setActiveCategory("all"); setActiveTag(""); }}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === "all" && !activeTag
                    ? "bg-[#FF6B6B] border-[#FF6B6B] text-white"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.slug); setActiveTag(""); }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-bold border transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug && !activeTag
                      ? "bg-[#FF6B6B] border-[#FF6B6B] text-white"
                      : "border-gray-200 text-gray-600"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-24">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-extrabold text-gray-700 mb-2">No quizzes found</h3>
                <p className="text-gray-400 text-sm">Try a different category or tag!</p>
                <button
                  onClick={() => { setActiveCategory("all"); setActiveTag(""); }}
                  className="mt-4 px-6 py-2.5 bg-[#FF6B6B] text-white text-sm font-bold rounded-full cursor-pointer whitespace-nowrap"
                >
                  Show All Quizzes
                </button>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {quizGroups.map((item, idx) => {
                    if (item === "ad") {
                      return (
                        <div key={`ad-grid-${idx}`} className="col-span-full w-full py-2 flex justify-center">
                          <AdSlot size="responsive" className="w-full" />
                        </div>
                      );
                    }
                    return <QuizCard key={(item as Quiz).id} quiz={item as Quiz} />;
                  })}
                  {/* Coming Soon Cards */}
                  {[1, 2, 3].map((i) => (
                    <div
                      key={`soon-${i}`}
                      className="bg-white border border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center justify-center text-center min-h-[280px]"
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-orange-50 text-[#FF6B6B] text-xl mb-3">
                        <i className="ri-time-line" />
                      </div>
                      <p className="font-bold text-gray-400 text-sm">New Quiz Coming Soon!</p>
                      <p className="text-gray-300 text-xs mt-1">Check back next week 🎉</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />

      {/* ── Ad: Sticky bottom banner (mobile only) ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex items-center justify-center py-1">
        <AdSlot size="sticky-mobile" />
      </div>
    </div>
  );
}
